import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { Control, Controller, useWatch } from "react-hook-form";
import { CreateCalendarEventForm } from "app/services/api/types/calendar/requests/CreateCalendarEventForm";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { SelectableOptionWithControl } from "../molecules/SelectableOptionWithControl";
import { CheckboxGroup } from "ui/gluestack/checkbox";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { format } from "date-fns";
import { DAYORDINALS, WEEKDAYS } from "app/constants/dateConstants";

interface Props {
  control: Control<CreateCalendarEventForm>;
}

export function MonthlyRecurrenceOptions({ control }: Props) {
  const startDate = useWatch({ control, name: "start_date" });
  const formattedStartDate = startDate ? format(startDate, "do") : "-";

  return (
    <Controller
      control={control}
      name="recurrence_rule.monthly.option"
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        return (
          <FormControl isInvalid={!!error}>
            <CheckboxGroup
              value={value ? [value] : []}
              onChange={(val) => {
                onChange(val.length ? val[val.length - 1] : undefined);
              }}
              className="flex-col gap-4"
            >
              <SelectableOptionWithControl
                value="specific_day"
                controlElement={
                  <View className="flex-row items-center flex-1 flex-wrap">
                    <Text className="text-sm text-gray-600 mr-2">
                      On the {formattedStartDate} of every
                    </Text>
                    <FormControlledInput
                      id="recurrence_rule.monthly.interval"
                      name="recurrence_rule.monthly.interval"
                      control={control}
                      className="flex-row items-center"
                      textInputClassName="flex-1 max-w-12"
                      isDisabled={value !== "specific_day"}
                      postfix={
                        <Text className="text-sm text-gray-600 mx-2">
                          month(s)
                        </Text>
                      }
                      rules={{
                        validate: {
                          requiredIf: (fieldValue) => {
                            if (value === "specific_day" && !fieldValue) {
                              return "Interval is required";
                            }
                            return true;
                          },
                        },
                      }}
                    />
                  </View>
                }
              />
              <SelectableOptionWithControl
                value="weekday_position"
                controlElement={
                  <View className="flex-row items-center flex-1 flex-wrap gap-2">
                    <FormControlledDropdown
                      name="recurrence_rule.monthly.by_set_position"
                      control={control}
                      className="flex-row items-center flex-1 "
                      placeholder="First"
                      isDisabled={value !== "weekday_position"}
                      options={DAYORDINALS}
                      dropdownClassName={{ width: 100 }}
                      prefix={
                        <Text className="text-sm text-gray-600 mr-2">The</Text>
                      }
                    />
                    <FormControlledDropdown
                      name="recurrence_rule.monthly.week_day"
                      control={control}
                      className="flex-row items-center flex-1"
                      placeholder="Day"
                      isDisabled={value !== "weekday_position"}
                      options={WEEKDAYS}
                      dropdownClassName={{ width: 130 }}
                    />
                    <Text className="text-sm text-gray-600 mx-2">of every</Text>
                    <FormControlledInput
                      id="recurrence_rule.monthly.interval"
                      name="recurrence_rule.monthly.interval"
                      control={control}
                      placeholder="0"
                      className="flex-row items-center"
                      textInputClassName="flex-1 max-w-12"
                      isDisabled={value !== "weekday_position"}
                    />
                    <Text className="text-sm text-gray-600 mx-2">month(s)</Text>
                  </View>
                }
              />
              <Controller
                name="recurrence_rule.monthly"
                control={control}
                render={({ fieldState: { error } }) => {
                  return (
                    <FormControl isInvalid={!!error}>
                      {error?.message && (
                        <FormControlError>
                          <FormControlErrorIcon
                            as={AlertCircleIcon}
                            className="text-error-500"
                            size="sm"
                          />
                          <FormControlErrorText className="text-error-500 text-sm">
                            {error.message}
                          </FormControlErrorText>
                        </FormControlError>
                      )}
                    </FormControl>
                  );
                }}
                rules={{
                  validate: {
                    requiredWeekDay: (value) => {
                      if (
                        value?.option === "weekday_position" &&
                        !value.week_day
                      ) {
                        return "Day is required";
                      }
                      return true;
                    },
                    requiredInterval: (value) => {
                      if (
                        value?.option === "weekday_position" &&
                        !value.interval
                      ) {
                        return "Interval is required";
                      }
                      return true;
                    },
                  },
                }}
              />
              <FormControlError>
                <FormControlErrorIcon
                  as={AlertCircleIcon}
                  className="text-error-500"
                  size="sm"
                />
                <FormControlErrorText className="text-error-500 text-sm">
                  {error?.message}
                </FormControlErrorText>
              </FormControlError>
            </CheckboxGroup>
          </FormControl>
        );
      }}
      rules={{
        required: requiredValidation("Monthly Interval"),
      }}
    />
  );
}
